import { memo } from 'react';

import { Controller } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import type { Column } from '@library';
import { TableRender, VStack, Text, Checkbox, FormField } from '@library';
import type { MatrixSelection as MatrixSelectionProps } from '../../../types/customStoryblokComponents';
import { useStoryblokContext } from '../../StoryblokFormContext';

interface StoryblokRowItem {
    id: string;
    label: string;
}

const MatrixSelection = memo(
    ({
        content,
        onChange,
        value,
    }: {
        content: MatrixSelectionProps;
        onChange: (value: string[] | string) => void;
        value: string[] | string;
    }) => {
        const { readonly } = useStoryblokContext();
        const columns: Column<StoryblokRowItem>[] = content.columns.map(
            (column, idx) => {
                return {
                    key: idx.toString(),
                    header: (
                        <VStack>
                            <Text>{column.label}</Text>
                        </VStack>
                    ),
                    render: item => {
                        const currentValue = (value || []) as string[];
                        const itemValue = item.id + '_' + column.value;
                        return (
                            <Checkbox
                                disabled={readonly}
                                checked={
                                    !!currentValue.find(
                                        selected => selected === itemValue
                                    )
                                }
                                onChange={checked => {
                                    if (checked) {
                                        onChange([...currentValue, itemValue]);
                                    } else {
                                        onChange(
                                            currentValue.filter(
                                                selected =>
                                                    selected !== itemValue
                                            )
                                        );
                                    }
                                }}
                            />
                        );
                    },
                };
            }
        );

        columns.unshift({
            key: 'label',
            header: '',
            render(item) {
                return <Text>{item.label}</Text>;
            },
        });

        const items = content.rows.map(item => {
            return { id: item.value, label: item.label };
        });

        return <TableRender items={items} columns={columns} compact />;
    }
);

const FormMatrixSelection = memo((props: MatrixSelectionProps) => {
    return (
        <Controller
            name={props.name || props._uid}
            render={({ field, fieldState: { error } }) => (
                <FormField
                    label={props.label}
                    error={
                        error?.message ? (
                            <FormattedMessage id={error.message} />
                        ) : undefined
                    }
                >
                    <div tabIndex={0} ref={field.ref}>
                        <MatrixSelection
                            content={props}
                            onChange={value => {
                                field.onChange(value);
                            }}
                            value={field.value}
                        />
                    </div>
                </FormField>
            )}
            rules={{
                required: props.required ? 'this field is required' : false,
            }}
        />
    );
});

export default memo(FormMatrixSelection);
